<template>
  <div>
    <ValidationObserver ref="form" v-slot="{ invalid }">
      <v-overlay :value="loading" absolute>
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
      <div v-if="docs" class="print-content">
        <v-subheader class="px-0 text-h6">保留登記號碼證</v-subheader>

        <!-- Basic Information -->
        <v-row dense>
          <v-col cols="12" md="4" lg="3" xl="2" sm="6">
            <ValidationProvider v-slot="{ errors }" name="發出日期" rules="required">
              <DateInputSeparate
                v-model="inputDate"
                label="發出日期 *"
                :error="!!errors.length"
                :error-messages="errors"
                dense
                flat
                hide-details="auto"
              />
            </ValidationProvider>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col cols="12" md="3" lg="2" xl="2" sm="6">
            <ValidationProvider v-slot="{ errors }" name="車牌號碼前綴" rules="required">
              <v-text-field
                v-model="vehno1"
                :error="!!errors.length"
                :error-messages="errors"
                flat
                label="車牌號碼 (前綴) *"
                outlined
                dense
                hide-details="auto"
              />
            </ValidationProvider>
          </v-col>
          <v-col cols="12" md="3" lg="2" xl="2" sm="6">
            <ValidationProvider v-slot="{ errors }" name="車牌號碼號碼" rules="required">
              <v-text-field
                v-model="vehno2"
                :error="!!errors.length"
                :error-messages="errors"
                flat
                label="車牌號碼 (號碼) *"
                outlined
                dense
                hide-details="auto"
              />
            </ValidationProvider>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col cols="12" md="6" lg="4" xl="3" sm="6">
            <SearchableCombobox
              v-model="docs.customer"
              path="v1/autocomplete-profiles"
              label="客戶"
              :params="{ filter: { type: 'client' }, searchPaths: ['name'] }"
              item-text="name"
              dense
              @selectItem="handleCustomerSelect"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                  <v-list-item-subtitle class="text-caption">{{ item.phone }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </SearchableCombobox>
          </v-col>
          <v-col cols="12" md="6" lg="4" xl="3" sm="6">
            <v-text-field
              v-model="docs.phone"
              label="電話"
              type="tel"
              autocomplete="off"
              outlined
              dense
              hide-details="auto"
            />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col cols="12" md="6" lg="4" xl="3" sm="6">
            <v-text-field
              v-model="docs.businessno"
              :error="businessnoError"
              :error-messages="businessnoError ? [businessnoErrorMessage] : []"
              :loading="checkingBusinessno"
              flat
              label="編號"
              outlined
              dense
              hide-details="auto"
              @blur="checkBusinessnoDuplicate"
            />
          </v-col>
          <v-col cols="12" md="6" lg="4" xl="3" sm="6">
            <v-autocomplete
              v-model="docs.filetype"
              :items="fileTypeOptions"
              flat
              label="文件類別"
              outlined
              clearable
              dense
              hide-details="auto"
            />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col cols="12" md="6" lg="4" xl="3" sm="6">
            <v-autocomplete
              v-model="docs.state"
              :items="stateOptions"
              flat
              label="狀態"
              outlined
              clearable
              dense
              item-text="text"
              item-value="value"
              hide-details="auto"
            />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col cols="12">
            <v-textarea
              v-model="docs.remark"
              flat
              label="備註說明"
              outlined
              rows="3"
              dense
              hide-details="auto"
            />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col cols="12" md="auto">
            <DateInputSeparate
              v-model="outDate"
              label="到期日期"
              readonly
              dense
              flat
              hide-details="auto"
            />
          </v-col>
        </v-row>

        <!-- Action Buttons -->
        <v-row dense class="mt-4 no-print">
          <v-col class="d-flex align-center gap-2">
            <v-btn
              :disabled="invalid || isMaster || saving"
              :loading="saving"
              color="primary"
              @click="save"
            >
              立即提交
            </v-btn>
            <v-spacer />

            <v-btn :disabled="saving" color="default" outlined @click="resetForm">重新填寫</v-btn>

            <v-btn
              v-if="!isNewRecord"
              :disabled="saving"
              color="error"
              outlined
              @click="confirmDelete"
            >
              刪除
            </v-btn>
          </v-col>
        </v-row>
      </div>
    </ValidationObserver>
  </div>
</template>

<script>
import api from '@/api';
import { crudRead, crudUpdate } from '@/api/crud';
import { mapGetters } from 'vuex';
import DateInputSeparate from '@/components/DateInputSeparate';
import SearchableCombobox from '@/components/SearchableCombobox';
import { RESTFUL, lpmsFileTypes } from '@/data/constants';
import { lpmsFileTypeOptions, lpmsStateOptions } from '@/data/optionsConstants';
import dayjs from 'dayjs';
import ApiError from '@/api/ApiError';

export default {
  name: 'LPMSDetailsForm',
  components: {
    DateInputSeparate,
    SearchableCombobox,
  },
  props: {
    id: {
      type: String,
      default: 'new',
    },
    initialData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      docs: null,
      vehno1: '',
      vehno2: '',
      fileTypeOptions: lpmsFileTypeOptions,
      stateOptions: lpmsStateOptions,
      loading: false,
      saving: false,
      checkingBusinessno: false,
      businessnoError: false,
      businessnoErrorMessage: '',
      isLoadingData: false, // Flag to prevent auto-calculation during data loading
    };
  },
  async mounted() {
    await this.reload();
  },
  computed: {
    ...mapGetters('auth', ['role', 'isMaster']),
    isNewRecord() {
      return !this.id || this.id === 'new';
    },
    inputDate: {
      get() {
        if (!this.docs?.inputDate) return null;
        return dayjs(this.docs.inputDate).format('YYYY-MM-DD');
      },
      set(value) {
        if (!value) return;
        this.docs.inputDate = dayjs(value, 'YYYY-MM-DD').startOf('day').toDate();
        // Auto-calculate outDate based on filetype (only if not loading data)
        if (!this.isLoadingData) {
          console.log('inputDate changed', value);
          this.calculateOutDate();
        }
      },
    },
    outDate: {
      get() {
        if (!this.docs?.outDate) return null;
        return dayjs(this.docs.outDate).format('YYYY-MM-DD');
      },
      set(value) {
        // Readonly, but keep setter for compatibility
        if (!value) return;
        this.docs.outDate = dayjs(value, 'YYYY-MM-DD').startOf('day').toDate();
      },
    },
  },
  watch: {
    vehno1(newVal) {
      this.updateVehno();
    },
    vehno2(newVal) {
      this.updateVehno();
    },
    async inputDate(newVal) {
      // Auto-generate businessno when inputDate is set
      if (this.isNewRecord && newVal) {
        await this.generateDefaultBusinessno();
      }
    },
    'docs.filetype'(newVal) {
      // Recalculate outDate when filetype changes (only if not loading data)
      if (!this.isLoadingData) {
        console.log('filetype changed', newVal);
        this.calculateOutDate();
      }
    },
  },
  methods: {
    calculateOutDate() {
      if (!this.docs?.inputDate) return;

      const inputDate = dayjs(this.docs.inputDate);
      // If filetype is 牌薄 (vehicleRegistrationBook), expiry is 2 years - 1 day
      // Otherwise, expiry is 1 year - 1 day
      const years = this.docs.filetype === lpmsFileTypes.vehicleRegistrationBook ? 2 : 1;
      this.docs.outDate = inputDate.add(years, 'year').add(-1, 'day').startOf('day').toDate();
    },
    handleCustomerSelect(customer) {
      // Only update related phone field when user clicks an item from dropdown
      if (customer && typeof customer === 'object') {
        this.docs.customer = customer.name || '';
        this.docs.phone = customer.phone || '';
      }
    },
    async checkBusinessnoDuplicate() {
      // Reset error state
      this.businessnoError = false;
      this.businessnoErrorMessage = '';

      // Skip if businessno is empty
      if (!this.docs.businessno) {
        return;
      }

      this.checkingBusinessno = true;
      try {
        const response = await api.get(RESTFUL.lpms.list, {
          params: {
            filter: { businessno: this.docs.businessno },
            limit: 1,
          },
        });

        const existingRecords = response.data.docs || response.data;

        // Check if duplicate exists
        // If editing, allow current record's businessno
        if (existingRecords.length > 0) {
          const isDuplicate = existingRecords.some(
            record => record._id !== this.id && record.businessno === this.docs.businessno,
          );

          if (isDuplicate) {
            this.businessnoError = true;
            this.businessnoErrorMessage = '此編號已存在，請使用其他編號';
          }
        }
      } catch (e) {
        console.error('Failed to check businessno duplicate:', e);
      } finally {
        this.checkingBusinessno = false;
      }
    },
    updateVehno() {
      // Combine vehno1 and vehno2 into docs.vehno
      if (this.vehno1 && this.vehno2) {
        this.docs.vehno = `${this.vehno1}${this.vehno2}`;
      } else {
        this.docs.vehno = '';
      }
    },
    splitVehno(vehno) {
      // Split vehno into prefix (letters) and number
      if (!vehno) return { prefix: '', number: '' };
      const match = vehno.match(/^([A-Za-z]+)(\d+)$/);
      if (match) {
        return { prefix: match[1], number: match[2] };
      }
      return { prefix: vehno, number: '' };
    },
    async generateDefaultBusinessno() {
      try {
        if (!this.docs.inputDate) return '';

        const inputDate = dayjs(this.docs.inputDate);

        const response = await api.get(RESTFUL.lpms.generateBusinessno, {
          params: {
            inputDate: inputDate.toISOString(),
          },
        });

        const businessno = response.data.businessno;
        if (!businessno) return '';

        this.docs.businessno = businessno;
        this.businessnoError = false;
        this.businessnoErrorMessage = '';

        return businessno;
      } catch (e) {
        console.error('Failed to generate businessno:', e);
        return '';
      }
    },
    async reload() {
      this.isLoadingData = true;
      try {
        if (this.isNewRecord) {
          // Initialize new record with default values
          const now = dayjs().startOf('day');

          this.docs = {
            inputDate: now.toDate(),
            outDate: null, // Will be calculated by calculateOutDate
            vehno: '',
            customer: '',
            phone: '',
            businessno: '',
            state: '',
            TEL1: '',
            TEL2: '',
            filetype: '',
            remark: '',
          };

          // Merge initialData if provided (for duplication)
          if (this.initialData) {
            Object.assign(this.docs, this.initialData);
          }

          // Calculate outDate based on filetype (for new records only)
          console.log('reload');
          this.calculateOutDate();

          // Split vehno into prefix and number
          const { prefix, number } = this.splitVehno(this.docs.vehno);
          this.vehno1 = prefix;
          this.vehno2 = number;

          // Generate default businessno after docs is initialized
          await this.generateDefaultBusinessno();
        } else {
          // Load existing record - use stored outDate from database
          this.docs = await crudRead(api, RESTFUL.lpms.read, this.id, {});

          // Split vehno
          const { prefix, number } = this.splitVehno(this.docs.vehno);
          this.vehno1 = prefix;
          this.vehno2 = number;
        }
      } finally {
        // Wait for Vue to process all reactive updates before resetting the flag
        await this.$nextTick();
        this.isLoadingData = false;
      }
    },
    resetForm() {
      // Reset form validation
      this.$refs.form?.reset();
      // Reload data
      this.reload();
    },
    async confirmDelete() {
      const confirmed = await this.$root.$confirm.open(
        '確認刪除',
        '確定要刪除此記錄嗎？此操作無法復原。',
        {
          color: 'red',
        },
      );
      if (!confirmed) {
        return;
      }
      this.deleteRecord();
    },
    async deleteRecord() {
      try {
        await api.delete(`${RESTFUL.lpms.read}/${this.id}`);

        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: '刪除成功',
          type: 'success',
          color: 'success',
        });

        // Emit deleted event
        this.$emit('deleted');
      } catch (e) {
        const ae = ApiError.wrap(e);
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: ae.message,
          type: 'error',
          color: 'error',
        });
      }
    },
    async save() {
      this.saving = true;
      try {
        if (!(await this.$refs.form?.validate?.())) {
          this.saving = false;
          return;
        }

        // Prepare payload
        const payload = {
          inputDate: this.docs.inputDate,
          outDate: this.docs.outDate,
          vehno: this.docs.vehno,
          customer: this.docs.customer,
          phone: this.docs.phone,
          businessno: this.docs.businessno,
          state: this.docs.state,
          filetype: this.docs.filetype,
          remark: this.docs.remark,
          TEL1: this.docs.TEL1,
          TEL2: this.docs.TEL2,
        };

        if (this.isNewRecord) {
          await api.post(RESTFUL.lpms.create, payload);
        } else {
          await crudUpdate(api, RESTFUL.lpms.read, this.id, payload);
        }

        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: '儲存成功',
          type: 'success',
          color: 'success',
        });

        // Emit saved event
        this.$emit('saved');
      } catch (e) {
        if (e.response?.data?.message === 'businessno already exists') {
          const confirmed = await this.$root.$confirm.open(
            '編號重複',
            '此編號已存在，是否要自動生成一個新的編號？',
            {
              color: 'warning',
            },
          );

          if (confirmed) {
            await this.generateDefaultBusinessno();
          }
          return;
        }
        const ae = ApiError.wrap(e);
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: ae.message,
          type: 'error',
          color: 'error',
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
